import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import barcodeService, { BarcodePrintSettings } from '../../services/barcodeService';
import { parseApiError } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './ProductCategoryEditPage.css';
import './BarcodeSettingsPage.css';
import { ArrowLeft, Save } from 'lucide-react';

// Allowed ranges — mirror the backend UpdateBarcodePrintSettingsDTO validation.
const LIMITS = {
  columns: { min: 1, max: 8 },
  rows: { min: 1, max: 25 },
  pageMarginMm: { min: 0, max: 30 },
  horizontalGapMm: { min: 0, max: 20 },
  verticalGapMm: { min: 0, max: 20 },
  barcodeHeightMm: { min: 5, max: 50 },
  fontSizePt: { min: 5, max: 14 },
} as const;

type NumericField = keyof typeof LIMITS;

interface FieldErrors {
  columns?: string;
  rows?: string;
  pageMarginMm?: string;
  horizontalGapMm?: string;
  verticalGapMm?: string;
  barcodeHeightMm?: string;
  fontSizePt?: string;
}

const DEFAULTS: BarcodePrintSettings = {
  columns: 4,
  rows: 10,
  pageMarginMm: 10,
  horizontalGapMm: 3,
  verticalGapMm: 3,
  barcodeHeightMm: 12,
  showCompanyName: false,
  showProductName: true,
  showSku: true,
  showPrice: true,
  fontSizePt: 7,
};

/**
 * Barcode label sheet designer: how many labels per A4 page (rows × columns),
 * spacing, barcode height and which text lines each label carries. The right
 * panel previews the sheet live while values change.
 */
const BarcodeSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formData, setFormData] = useState<BarcodePrintSettings>(DEFAULTS);

  useEffect(() => {
    barcodeService.getSettings()
      .then((res) => { if (res.data.data) setFormData(res.data.data); })
      .catch((err: unknown) => setError(parseApiError(err, t('barcode.failedToLoad')).message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value === '' ? 0 : parseFloat(value) }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleToggle = (name: keyof BarcodePrintSettings) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [name]: e.target.checked }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    (Object.keys(LIMITS) as NumericField[]).forEach((field) => {
      const { min, max } = LIMITS[field];
      const value = formData[field] as number;
      if (Number.isNaN(value) || value < min || value > max) {
        errors[field] = t('barcode.formValidation.range', { min, max });
        isValid = false;
      }
    });

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    if (!validateForm()) return;

    setSaving(true);
    try {
      const res = await barcodeService.updateSettings(formData);
      if (res.data.data) setFormData(res.data.data);
      setMessage(t('barcode.updateSuccess'));
    } catch (err: unknown) {
      setError(parseApiError(err, t('barcode.failedToUpdate')).message);
    } finally {
      setSaving(false);
    }
  };

  const numberField = (
    name: NumericField, label: string, hint: string, step = 1
  ) => (
    <div className="form-group">
      <label className="form-label">
        {label} <span className="required">*</span>
      </label>
      <input
        type="number"
        className={`form-input ${fieldErrors[name] ? 'is-invalid' : ''}`}
        name={name}
        value={formData[name]}
        onChange={handleNumberChange}
        min={LIMITS[name].min}
        max={LIMITS[name].max}
        step={step}
      />
      {fieldErrors[name] && <small className="form-error">{fieldErrors[name]}</small>}
      {!fieldErrors[name] && <small className="form-helper">{hint}</small>}
    </div>
  );

  const toggleField = (name: keyof BarcodePrintSettings, label: string) => (
    <div className="form-check">
      <input
        type="checkbox"
        className="form-check-input"
        id={`barcode-${String(name)}`}
        checked={Boolean(formData[name])}
        onChange={handleToggle(name)}
      />
      <label className="form-check-label" htmlFor={`barcode-${String(name)}`}>
        {label}
      </label>
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="edit-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/products')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('barcode.settingsTitle')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {message && (
          <div className="alert alert-success alert-dismissible fade show">
            {message}
            <button type="button" className="btn-close" onClick={() => setMessage(null)} aria-label={t('common.close')}></button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} aria-label={t('common.close')}></button>
          </div>
        )}

        <div className="edit-content">
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('barcode.sheetLayout')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit} noValidate>
                  <div className="barcode-settings-grid">
                    {numberField('columns', t('barcode.columns'), t('barcode.columnsHint'))}
                    {numberField('rows', t('barcode.rows'), t('barcode.rowsHint'))}
                    {numberField('pageMarginMm', t('barcode.pageMargin'), t('barcode.pageMarginHint'), 0.5)}
                    {numberField('barcodeHeightMm', t('barcode.barcodeHeight'), t('barcode.barcodeHeightHint'), 0.5)}
                    {numberField('horizontalGapMm', t('barcode.horizontalGap'), t('barcode.gapHint'), 0.5)}
                    {numberField('verticalGapMm', t('barcode.verticalGap'), t('barcode.gapHint'), 0.5)}
                    {numberField('fontSizePt', t('barcode.fontSize'), t('barcode.fontSizeHint'))}
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('barcode.labelContent')}</label>
                    {toggleField('showCompanyName', t('barcode.showCompanyName'))}
                    {toggleField('showProductName', t('barcode.showProductName'))}
                    {toggleField('showSku', t('barcode.showSku'))}
                    {toggleField('showPrice', t('barcode.showPrice'))}
                    <small className="form-helper">{t('barcode.labelContentHint')}</small>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('barcode.preview')}</h3>
              </div>
              <div className="edit-card-body">
                {/* A4 sheet preview, proportional to 210 × 297 mm */}
                <div
                  className="barcode-sheet-preview"
                  style={{ padding: `${(formData.pageMarginMm / 297) * 100}% ${(formData.pageMarginMm / 210) * 100}%` }}
                >
                  <div
                    className="barcode-sheet-grid"
                    style={{
                      gridTemplateColumns: `repeat(${Math.max(1, formData.columns)}, 1fr)`,
                      gridTemplateRows: `repeat(${Math.max(1, formData.rows)}, 1fr)`,
                      columnGap: `${(formData.horizontalGapMm / 210) * 100}%`,
                      rowGap: `${(formData.verticalGapMm / 297) * 100}%`,
                    }}
                  >
                    {Array.from({ length: Math.min(formData.columns * formData.rows, 200) }).map((_, i) => (
                      <div className="barcode-label-cell" key={i}>
                        {formData.showCompanyName && <span className="barcode-label-line">{t('barcode.sampleCompany')}</span>}
                        {formData.showProductName && <span className="barcode-label-line">{t('barcode.sampleProduct')}</span>}
                        <span className="barcode-label-bars" />
                        {formData.showSku && <span className="barcode-label-line">SKU-001234</span>}
                        {formData.showPrice && <span className="barcode-label-line barcode-label-price">USD 9.99</span>}
                      </div>
                    ))}
                  </div>
                </div>
                <p className="form-helper" style={{ marginTop: '10px' }}>
                  {t('barcode.previewHint', { count: formData.columns * formData.rows })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BarcodeSettingsPage;
